import { createContext, useContext, useState, useEffect, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import i18n from "../i18n/i18n"

type LanguageContextType = {
  language: "en" | "ar"
  toggleLanguage: () => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Initialize with the current i18n language
  const [language, setLanguage] = useState<"en" | "ar">(i18n.language as "en" | "ar" || "en")

  // Sync language state with i18n
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setLanguage(lng as "en" | "ar")
    }

    // Listen for language changes
    i18n.on("languageChanged", handleLanguageChanged)

    return () => {
      i18n.off("languageChanged", handleLanguageChanged)
    }
  }, [])

  // Toggle language using i18n
  const toggleLanguage = () => {
    const newLanguage = language === "en" ? "ar" : "en"
    i18n.changeLanguage(newLanguage)
    // i18n will handle saving to localStorage through its detection plugin
  }

  return <LanguageContext.Provider value={{ language, toggleLanguage }}>{children}</LanguageContext.Provider>
}

// Custom hook to use the language context
export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

// Export a combined hook for both language context and translations
export function useTranslationWithLanguage() {
  const { t } = useTranslation()
  const languageContext = useLanguage()

  return {
    t,
    ...languageContext
  }
}